import { useState, useEffect } from 'react';
import { Button } from '../primitives/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../primitives/dialog';
import { Switch } from '../primitives/switch';
import { useConsent } from './consent-context';

/**
 * ConsentSettings — preferences modal opened from the banner's "Manage
 * preferences" button or the footer "Cookie settings" link. Renders the
 * three categories with per-category toggles. Necessary is always-on
 * and disabled.
 */
export function ConsentSettings() {
  const { isOpen, close, state, save } = useConsent();
  const [analytics, setAnalytics] = useState(state?.analytics ?? false);
  const [marketing, setMarketing] = useState(state?.marketing ?? false);

  useEffect(() => {
    if (isOpen) {
      setAnalytics(state?.analytics ?? false);
      setMarketing(state?.marketing ?? false);
    }
  }, [isOpen, state]);

  return (
    <Dialog open={isOpen} onOpenChange={(o) => (o ? null : close())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Cookie preferences</DialogTitle>
          <DialogDescription>
            Choose which categories of cookies and tracking technologies you allow on inferrex.com.
            Changes apply immediately and are remembered on this device.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-3">
          <CategoryRow
            title="Strictly necessary"
            description="Required for the site to work: session, security, payment-session cookies. Cannot be disabled."
            checked
            disabled
          />
          <CategoryRow
            title="Analytics"
            description="Google Analytics 4 — anonymous traffic + usage measurement to help us improve the site. No personal identifiers."
            checked={analytics}
            onCheckedChange={setAnalytics}
          />
          <CategoryRow
            title="Marketing"
            description="Reserved for future use — remarketing tags, ads attribution. Currently no marketing tags are active even if granted."
            checked={marketing}
            onCheckedChange={setMarketing}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => save({ analytics, marketing })}>Save preferences</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface CategoryRowProps {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

function CategoryRow({ title, description, checked, disabled, onCheckedChange }: CategoryRowProps) {
  const switchProps = onCheckedChange ? { onCheckedChange } : {};
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-border bg-card/50 p-4">
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} disabled={disabled ?? false} {...switchProps} />
    </div>
  );
}
